import { AxiosInstance } from 'axios';
import {
  createAxiosInstance,
  attachAuthInterceptors,
} from '@/application/helpers/APIRequest';

const customerCreditAPI: AxiosInstance = createAxiosInstance({
  baseUrlKey: 'CUSTOMER_CREDIT_API_BASE_URL',
});
attachAuthInterceptors(customerCreditAPI);

const isAbortLike = (error: unknown) => {
  const e = error as { code?: string; name?: string };
  return (
    e?.code === 'ERR_CANCELED' ||
    e?.name === 'CanceledError' ||
    e?.name === 'AbortError'
  );
};

// Types
export interface GetBusinessBanksRequest {
  page?: number;
  limit?: number;
  bank_name?: string;
  is_active?: boolean | string;
  account_name?: string;
  account_name_th?: string;
  account_name_en?: string;
  account_number?: string;
  currency?: string;
}

export interface BusinessBankData {
  id: string;
  business_profile_id: string;
  bank_name: string;
  bank_name_value?: string;
  bank_img?: string;
  account_name: string;
  account_name_th?: string;
  account_name_en?: string;
  account_number: string;
  currency: string;
  currency_value?: string;
  is_active: boolean;
  is_main_account: boolean;
  type: string;
  type_value?: string;
  created_at: string;
  updated_at: string;
}

export interface GetBusinessBanksResponse {
  success: boolean;
  data?: {
    data: BusinessBankData[];
    total: number;
    limit: number;
    page: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
  message?: string;
}

export interface ValidateDeleteBusinessBankResponse {
  success: boolean;
  code?: string;
  message?: string;
  status?: number;
  data?: {
    is_valid: boolean;
    delete_main_value?: boolean;
    active_accounts?: BusinessBankData[];
  };
}

export interface DeleteBusinessBankRequest {
  validation_response: {
    is_valid: boolean;
  };
}

export interface DeleteBusinessBankResponse {
  success: boolean;
  message?: string;
}

export interface ValidateCreateBusinessBankRequest {
  type: string;
  currency: string;
  bank_name: string;
  account_name_th: string;
  account_name_en: string;
  account_number: string;
  is_active: boolean;
  is_main_account: boolean;
}

export interface ValidateCreateBusinessBankResponse {
  success: boolean;
  data?: {
    is_valid: boolean;
    found_similar_account_name: boolean;
    main_value_already_exists: boolean;
    similar_account_names?: BusinessBankData[];
    existing_main_account?: BusinessBankData;
  };
  message?: string;
  code?: string;
}

export interface ValidateUpdateBusinessBankRequest {
  bank_name: string;
  account_name_th: string;
  account_name_en: string;
  account_number: string;
  currency: string;
  is_active: boolean;
  is_main_account: boolean;
  type: string;
}

export interface ValidateUpdateBusinessBankResponse {
  success: boolean;
  data?: {
    is_valid: boolean;
    found_similar_account_name: boolean;
    main_value_already_exists: boolean;
    edit_main_value?: boolean;
    similar_account_names?: BusinessBankData[];
    existing_main_account?: BusinessBankData;
    active_accounts?: BusinessBankData[];
  };
  message?: string;
  code?: string;
}

// API Functions
export const getBusinessBanks = async (
  params: GetBusinessBanksRequest
): Promise<GetBusinessBanksResponse> => {
  try {
    const {
      page = 1,
      limit = 10,
      bank_name,
      is_active,
      account_name_th,
      account_name_en,
      account_name,
      account_number,
      currency,
    } = params;
    const queryParams = new URLSearchParams();

    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    if (bank_name) {
      queryParams.append('bank_name', bank_name);
    }
    if (is_active !== undefined && is_active !== '') {
      const isActiveValue =
        typeof is_active === 'boolean'
          ? is_active
          : is_active === 'active' || is_active === 'true';
      queryParams.append('is_active', isActiveValue.toString());
    }
    if (account_name_th) {
      queryParams.append('account_name_th', account_name_th);
    }
    if (account_name_en) {
      queryParams.append('account_name_en', account_name_en);
    }
    if (account_name) {
      queryParams.append('account_name', account_name);
    }
    if (account_number) {
      queryParams.append('account_number', account_number);
    }
    if (currency) {
      queryParams.append('currency', currency);
    }

    const response = await customerCreditAPI.get(
      `/business-banks?${queryParams.toString()}`
    );

    return {
      success: true,
      data: {
        data: response.data.data || [],
        total: response.data.total,
        limit: response.data.limit,
        page: response.data.page,
        total_pages: response.data.total_pages,
        has_next: response.data.has_next || false,
        has_previous: response.data.has_previous || false,
      },
      message: 'Business banks fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch business banks',
    };
  }
};

export const getBusinessBankById = async (
  id: string,
  opts?: { signal?: AbortSignal; skipGlobal500ErrorToast?: boolean }
): Promise<{
  success: boolean;
  data?: BusinessBankData;
  message?: string;
  httpStatus?: number;
}> => {
  try {
    const response = await customerCreditAPI.get(`/business-banks/${id}`, {
      signal: opts?.signal,
      skipGlobal500ErrorToast: opts?.skipGlobal500ErrorToast === true,
    });

    return {
      success: true,
      data: response.data.data || response.data,
      message: 'Business bank fetched successfully',
    };
  } catch (error: any) {
    if (isAbortLike(error)) throw error;
    const rawStatus = error.response?.status;
    return {
      success: false,
      httpStatus: rawStatus != null ? Number(rawStatus) : undefined,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch business bank',
    };
  }
};

export const validateDeleteBusinessBank = async (
  id: string
): Promise<ValidateDeleteBusinessBankResponse> => {
  try {
    const response = await customerCreditAPI.post(
      `/business-banks/${id}/validate-delete`
    );

    return {
      success: true,
      message: 'Validation passed',
      data: response.data,
    };
  } catch (error: any) {
    return {
      success: false,
      code: error.response?.data?.code,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to validate delete',
      status: error.response?.status,
      data: error.response?.data,
    };
  }
};

export const deleteBusinessBank = async (
  id: string,
  data: DeleteBusinessBankRequest
): Promise<DeleteBusinessBankResponse> => {
  try {
    await customerCreditAPI.delete(`/business-banks/${id}`, {
      data,
    });

    return {
      success: true,
      message: 'Business bank deleted successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to delete business bank',
    };
  }
};

export const validateCreateBusinessBank = async (
  data: ValidateCreateBusinessBankRequest
): Promise<ValidateCreateBusinessBankResponse> => {
  try {
    const response = await customerCreditAPI.post(
      '/business-banks/validate-create',
      data
    );

    return {
      success: true,
      data: response.data,
      message: 'Validation completed successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      code: error.response?.data?.code,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to validate business bank',
      data: error.response?.data?.data,
    };
  }
};

export interface CreateBusinessBankData {
  type: string;
  currency: string;
  bank_name: string;
  account_name_th: string;
  account_name_en: string;
  account_number: string;
  is_active: boolean;
  is_main_account: boolean;
}

export interface CreateBusinessBankRequest {
  data: CreateBusinessBankData;
  validation_response: {
    is_valid: boolean;
    found_similar_account_name?: boolean;
    main_value_already_exists?: boolean;
    similar_account_names?: BusinessBankData[];
    existing_main_account?: BusinessBankData;
  };
}

export interface CreateBusinessBankResponse {
  success: boolean;
  data?: BusinessBankData;
  message?: string;
}

export const createBusinessBank = async (
  request: CreateBusinessBankRequest
): Promise<CreateBusinessBankResponse> => {
  try {
    const response = await customerCreditAPI.post('/business-banks', request);

    return {
      success: true,
      data: response.data.data || response.data,
      message: 'Business bank created successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to create business bank',
    };
  }
};

export const validateUpdateBusinessBank = async (
  id: string,
  data: ValidateUpdateBusinessBankRequest
): Promise<ValidateUpdateBusinessBankResponse> => {
  try {
    const response = await customerCreditAPI.post(
      `/business-banks/${id}/validate-update`,
      data
    );

    return {
      success: true,
      data: response.data,
      message: 'Validation completed successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      code: error.response?.data?.code,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to validate business bank update',
      data: error.response?.data?.data,
    };
  }
};

export interface UpdateBusinessBankData {
  type: string;
  currency: string;
  bank_name: string;
  account_name_th: string;
  account_name_en: string;
  account_number: string;
  is_active: boolean;
  is_main_account: boolean;
}

export interface UpdateBusinessBankRequest {
  data: UpdateBusinessBankData;
  validation_response: {
    is_valid: boolean;
    found_similar_account_name?: boolean;
    main_value_already_exists?: boolean;
    similar_account_names?: BusinessBankData[];
    existing_main_account?: BusinessBankData;
  };
}

export interface UpdateBusinessBankResponse {
  success: boolean;
  data?: BusinessBankData;
  message?: string;
}

export const updateBusinessBank = async (
  id: string,
  request: UpdateBusinessBankRequest
): Promise<UpdateBusinessBankResponse> => {
  try {
    const response = await customerCreditAPI.put(
      `/business-banks/${id}`,
      request
    );

    return {
      success: true,
      data: response.data.data || response.data,
      message: 'Business bank updated successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update business bank',
    };
  }
};

export interface UpdateBusinessBankWithReplaceRequest {
  data: {
    id_to_update: string;
    update_data: UpdateBusinessBankData;
    new_main_account_id: string;
  };
  validation_response: {
    is_valid: boolean;
    found_similar_account_name?: boolean;
    edit_main_value?: boolean;
    main_value_already_exists?: boolean;
    similar_account_names?: BusinessBankData[];
    existing_main_account?: BusinessBankData;
    active_accounts?: BusinessBankData[];
  };
}

export interface UpdateBusinessBankWithReplaceResponse {
  success: boolean;
  data?: BusinessBankData;
  message?: string;
}

export const updateBusinessBankWithReplace = async (
  request: UpdateBusinessBankWithReplaceRequest
): Promise<UpdateBusinessBankWithReplaceResponse> => {
  try {
    const response = await customerCreditAPI.put(
      '/business-banks/update-with-replace',
      request
    );

    return {
      success: true,
      data: response.data.data || response.data,
      message: 'Business bank updated with replace successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to update business bank with replace',
    };
  }
};

export interface DeleteBusinessBankWithReplaceRequest {
  data: {
    id_to_delete: string;
    new_main_account_id: string;
    delete_main_value: boolean;
  };
  validation_response: {
    is_valid: boolean;
    delete_main_value: boolean;
    active_accounts?: BusinessBankData[];
  };
}

export interface DeleteBusinessBankWithReplaceResponse {
  success: boolean;
  message?: string;
}

export const deleteBusinessBankWithReplace = async (
  request: DeleteBusinessBankWithReplaceRequest
): Promise<DeleteBusinessBankWithReplaceResponse> => {
  try {
    await customerCreditAPI.delete('/business-banks/delete-with-replace', {
      data: request,
    });

    return {
      success: true,
      message: 'Business bank deleted with replace successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to delete business bank with replace',
    };
  }
};

export interface BankNameConfig {
  id: string;
  value: string;
  bank_name: string;
  bank_img?: string;
  display_order: number;
}

export interface GetBankNamesResponse {
  success: boolean;
  data?: BankNameConfig[];
  message?: string;
}

export const getBankNames = async (): Promise<GetBankNamesResponse> => {
  try {
    const response = await customerCreditAPI.get(
      '/business-banks/config/bank-names'
    );

    return {
      success: true,
      data: response.data.data || response.data || [],
      message: 'Bank names fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch bank names',
    };
  }
};

export interface TypeConfig {
  id: string;
  value: string;
  type: string;
  display_order?: number;
}

export interface GetTypesResponse {
  success: boolean;
  data?: TypeConfig[];
  message?: string;
}

export const getTypes = async (): Promise<GetTypesResponse> => {
  try {
    const response = await customerCreditAPI.get(
      '/business-banks/config/types'
    );

    return {
      success: true,
      data: response.data.data || response.data || [],
      message: 'Types fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch types',
    };
  }
};

export interface CurrencyConfig {
  id: string;
  value: string;
  currency: string;
  display_order?: number;
}

export interface GetCurrenciesResponse {
  success: boolean;
  data?: CurrencyConfig[];
  message?: string;
}

export const getCurrencies = async (): Promise<GetCurrenciesResponse> => {
  try {
    const response = await customerCreditAPI.get(
      '/business-banks/config/currencies'
    );

    return {
      success: true,
      data: response.data.data || response.data || [],
      message: 'Currencies fetched successfully',
    };
  } catch (error: any) {
    return {
      success: false,
      message:
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch currencies',
    };
  }
};

export default customerCreditAPI;
